<template>
    <div class="distribution_card">
        <div class="card_header is-flex is-align-items-center">
            <PieChartIcon class="header_icon" />
            <span class="card_title">{{ title }}</span>
        </div>

        <div v-if="hasDistributions" class="distributions_grid">
            <div v-for="dist in distributions" :key="dist.category_id"
                class="distribution_row">
                <div class="col_label">{{ dist.category_label }}</div>
                <div class="distribution_body">
                    <div class="donut_container" :style="donutStyle">
                        <EChart v-if="chartReady" :option="chartOption(dist)"
                            @chart-click="onChartClick(dist, $event)"
                            @chart-mouseover="onChartHover(dist, $event)"
                            @chart-mouseout="onChartLeave" />
                    </div>
                    <ul class="ranking_list">
                        <li v-for="(item, idx) in dist.items" :key="item.name"
                            class="ranking_item"
                            :class="{
                                'is-focused': isFocused(dist, item.name),
                                'is-selected': isSelected(dist, item.name),
                            }"
                            role="button"
                            tabindex="0"
                            @click="onItemClick(dist, item.name)"
                            @keydown.enter.prevent="onItemClick(dist, item.name)"
                            @keydown.space.prevent="onItemClick(dist, item.name)"
                            @mouseenter="onItemEnter(dist, item.name)"
                            @mouseleave="onItemLeave">
                            <span class="ranking_swatch" aria-hidden="true"
                                :style="{ background: palette(dist)[idx] }"></span>
                            <span class="ranking_label">{{ item.name }}</span>
                            <span class="ranking_leader" aria-hidden="true"></span>
                            <span class="ranking_meta">
                                <span class="ranking_amount">{{ formatMoney(item.amount) }}</span>
                                <span class="ranking_dot" aria-hidden="true">·</span>
                                <span class="ranking_share">{{ formatShare(item.share) }}</span>
                            </span>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
        <p v-else class="empty_state">
            Sin categorías clasificadas para mostrar distribución.
        </p>
    </div>
</template>

<script>
import EChart from "@/components/charts/EChart.vue";
import PieChartIcon from "@/components/svg/PieChartIcon.vue";
import { parseToMoney } from "@/utils/currency";
import { formatShare } from "@/utils/percentage";
import { widenedSeriesPalette } from "@/utils/colorPalette";

// Anchor tokens — leídos por getComputedStyle en mounted porque el canvas
// de ECharts no resuelve var(...). Mantener este par como los extremos del
// gradiente del que se interpolan N colores; el theme define el rango.
const LIGHTEST_ANCHOR = "--report-chart-segment-3";
const DARKEST_ANCHOR = "--report-chart-segment-1";

// Tamaño fijo del donut (px). Única fuente de verdad para dimensionar el
// contenedor inline (el EChart wrapper difiere su render hasta que el host
// mide > 0, así que no hace falta pasarle init-options explícitas). El donut
// es la única visualización del share (la lista es leyenda), así que se le da
// más presencia.
const DONUT_PX = 180;

export default {
    name: "StoresDistributionCard",
    components: { EChart, PieChartIcon },
    props: {
        // Lista de distribuciones — una entrada por categoría configurada.
        // Backend devuelve hasta 2. Cada entry: {category_id, category_label,
        // items: [{name, amount, share}]}.
        distributions: {
            type: Array,
            required: true,
            validator: (arr) => arr.every(
                (d) => typeof d.category_id === "string" && Array.isArray(d.items),
            ),
        },
        // Selección persistente (click) — `{ category_id, value }` o null. La
        // dueña del estado es la vista (BodySalesAnalysis), que filtra el ranking
        // de clientes; acá sólo pinta el item seleccionado y emite el toggle.
        selectedCategory: { type: Object, default: null },
    },
    data() {
        // chartReady arranca false porque necesitamos resolver los anchor
        // tokens vía getComputedStyle antes del primer render. Evita el flash
        // del donut sin paleta y el caso "los anchors no existen" (theme mal
        // configurado): si el palette queda vacío, no rendereamos el chart.
        // `focusedCategory`: cuando un item está hovereado (chart o lista),
        // emitimos arriba qué categoría + valor, para que el ranking de
        // clientes resalte las contrapartes asociadas a ese atributo.
        return {
            chartReady: false,
            lightAnchor: "",
            darkAnchor: "",
            focusedCategory: null, // { category_id, value } | null
        };
    },
    computed: {
        title() {
            // Card apila varias distribuciones (cuenta + categorías), cada una
            // con su propio label de fila — el título de la card es genérico.
            return "Distribución de ventas";
        },
        hasDistributions() {
            return this.distributions.some((d) => d.items.length > 0);
        },
        donutStyle() {
            return { width: `${DONUT_PX}px`, height: `${DONUT_PX}px` };
        },
    },
    mounted() {
        this.resolveCssColors();
        this.chartReady = !!this.lightAnchor && !!this.darkAnchor;
    },
    methods: {
        resolveCssColors() {
            if (typeof window === "undefined") return;
            // Leemos los tokens del subárbol (this.$el), no de :root: la paleta
            // vive en el [data-theme] del contenedor (tab/drawer) y se hereda por
            // cascada, así el donut toma los anchors del tab en vez del default.
            // Mismo patrón que MonthlyHistoryCard/SalesComparisonChart.
            const host = this.$el && this.$el.nodeType === 1
                ? this.$el
                : (this.$el && this.$el.parentElement) || document.documentElement;
            const root = getComputedStyle(host);
            this.lightAnchor = root.getPropertyValue(LIGHTEST_ANCHOR).trim();
            this.darkAnchor = root.getPropertyValue(DARKEST_ANCHOR).trim();
        },
        palette(dist) {
            // N colores interpolados entre dark y light — el item con más
            // share va más claro, los menores más oscuros (matchea el donut).
            if (!this.lightAnchor || !this.darkAnchor) return [];
            return widenedSeriesPalette(
                this.lightAnchor, this.darkAnchor, dist.items.length,
            );
        },
        chartOption(dist) {
            const palette = this.palette(dist);
            return {
                tooltip: {
                    trigger: "item",
                    // El donut es chico y vive en una card con overflow:hidden
                    // (y dentro del modal). Sin esto el tooltip se recorta:
                    // appendToBody lo monta como hijo de <body> para que escape
                    // el clipping, y confine:false lo deja posicionarse libre.
                    appendToBody: true,
                    confine: false,
                    formatter: (params) => {
                        // `percent` de ECharts sale sobre la suma de |montos|
                        // (los slices llevan la magnitud) — coincide con el
                        // share del backend. El monto se muestra con su signo.
                        const share = Math.round(params.percent ?? 0);
                        const amount = params.data?.signedAmount ?? params.value;
                        return `<div class="echart_tooltip">
                            <strong>${params.name}</strong><br>
                            ${parseToMoney(amount)} · ${share}%
                        </div>`;
                    },
                },
                series: [
                    {
                        type: "pie",
                        radius: ["62%", "92%"],
                        padAngle: 3,
                        avoidLabelOverlap: false,
                        label: { show: false },
                        labelLine: { show: false },
                        itemStyle: { borderRadius: 3 },
                        // Un item negativo (NC mayor que las ventas del
                        // atributo) no se puede dibujar como slice: el donut
                        // pinta la magnitud y guarda el monto signado para el
                        // tooltip.
                        data: dist.items.map((s, i) => ({
                            value: Math.abs(s.amount ?? 0),
                            signedAmount: s.amount,
                            name: s.name,
                            itemStyle: { color: palette[i] },
                        })),
                    },
                ],
            };
        },
        // Hover handlers — chart (mouseover sobre slice) y lista (mouseenter
        // sobre item) convergen al mismo state, así el highlight es
        // consistente. mouseleave en cualquiera limpia.
        onChartHover(dist, params) {
            // params.name = label del slice hovereado.
            this.setFocus(dist.category_id, params.name);
        },
        onChartLeave() {
            this.clearFocus();
        },
        onItemEnter(dist, value) {
            this.setFocus(dist.category_id, value);
        },
        onItemLeave() {
            this.clearFocus();
        },
        setFocus(categoryId, value) {
            this.focusedCategory = { category_id: categoryId, value };
            this.$emit("focus-category", this.focusedCategory);
        },
        clearFocus() {
            this.focusedCategory = null;
            this.$emit("blur-category");
        },
        isFocused(dist, value) {
            return this.focusedCategory
                && this.focusedCategory.category_id === dist.category_id
                && this.focusedCategory.value === value;
        },
        // Click (lista o slice) → toggle de selección persistente. La vista
        // padre decide si activa o limpia (si se reclickea el mismo).
        onItemClick(dist, value) {
            this.$emit("select-category", { category_id: dist.category_id, value });
        },
        onChartClick(dist, params) {
            if (params && params.name) {
                this.$emit("select-category", { category_id: dist.category_id, value: params.name });
            }
        },
        isSelected(dist, value) {
            return this.selectedCategory
                && this.selectedCategory.category_id === dist.category_id
                && this.selectedCategory.value === value;
        },
        formatMoney(v) {
            return parseToMoney(v);
        },
        // Share signado (ver utils/percentage): el label conserva el signo,
        // el donut pinta la magnitud.
        formatShare(share) {
            return formatShare(share);
        },
    },
};
</script>

<style scoped lang="scss">
.distribution_card {
    background: var(--report-bg);
    border-radius: 16px;
    border: 1px solid var(--report-border);
    box-shadow: var(--report-shadow-card);
    padding: 16px;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.card_header {
    // Más aire entre el título "Distribución de ventas" y su contenido, para
    // que el header respire igual que el bloque de monto del primer card.
    margin-bottom: 20px;
    color: var(--report-text);
    flex-shrink: 0;

    .header_icon {
        margin-right: 8px;
        flex-shrink: 0;
    }

    .card_title {
        font-size: 14px;
        font-weight: 600;
    }
}

// Stack vertical de distribuciones (cuenta + categorías). El scroll vive en
// el contenedor: cada fila se autodimensiona y la card scrollea si la suma
// excede el alto disponible.
.distributions_grid {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 20px;
}

// Cada distribución: label arriba (full width) + body horizontal
// (donut izquierda, detalle derecha). Separador suave entre filas.
.distribution_row {
    display: flex;
    flex-direction: column;
    min-width: 0;

    & + & {
        padding-top: 20px;
        border-top: 1px solid var(--report-divider-soft);
    }
}

.col_label {
    font-size: 12px;
    font-weight: 600;
    color: var(--report-text-muted-strong);
    text-transform: capitalize;
    margin-bottom: 8px;
    flex-shrink: 0;
}

// Donut a la izquierda, lista de detalle a la derecha. Gap algo más amplio para
// que la leyenda respire del donut (ahora más grande).
.distribution_body {
    display: flex;
    align-items: center;
    gap: 40px;
    min-width: 0;
}

// Donut cuadrado a la izquierda — no se comprime. El tamaño (width/height) lo
// pinta inline desde DONUT_PX (única fuente de verdad, compartida con las
// init-options de ECharts); acá sólo el comportamiento de layout.
.donut_container {
    flex-shrink: 0;
}

.ranking_list {
    list-style: none;
    margin: 0;
    padding: 0;
    flex: 1;
    min-width: 0;
    // Cap de ancho: en pantallas anchas el dot-leader se estiraba y alejaba el
    // monto/% del nombre. Acotando la leyenda el par nombre→monto queda compacto
    // y fácil de leer; el sobrante de ancho queda como aire a la derecha.
    max-width: 390px;
}

// Fila de leyenda: swatch (color del segmento del donut) · nombre a la
// izquierda · línea de puntos (leader) que rellena el hueco · monto+%
// flush-right. La proporción ya la cuenta el donut — acá no se repite con una
// barra. Flex (no grid) para que el nombre tome su ancho de contenido y el
// leader absorba el resto hasta las cifras.
.ranking_item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 5px 6px;
    font-size: 13px;
    border-radius: 6px;
    cursor: pointer;
    transition: background-color 120ms ease;

    &:hover,
    &.is-focused {
        background: var(--report-surface);
    }

    // Selección persistente (click) — acento, por encima del hover transitorio.
    &.is-selected {
        background: var(--report-accent-bg);
        box-shadow: inset 0 0 0 1px var(--report-accent-border);

        .ranking_label { color: var(--report-accent-text); font-weight: 600; }
    }
}

// Dot leader: ayuda visual discreta que conecta el nombre con su monto. Usa el
// divisor más tenue del theme y se apoya cerca de la baseline del texto.
.ranking_leader {
    flex: 1 1 auto;
    min-width: 12px;
    // Nudge hacia abajo desde el centro de la fila para apoyar la línea cerca
    // de la baseline del texto, como un leader de índice.
    transform: translateY(3px);
    border-bottom: 1px dotted var(--report-divider-soft);
}

// Marca de color que liga la fila a su segmento del donut (misma paleta, mismo
// índice). Radius chico por ser un elemento pequeño.
.ranking_swatch {
    width: 10px;
    height: 10px;
    border-radius: 3px;
    flex-shrink: 0;
}

.ranking_label {
    flex: 0 1 auto;
    min-width: 0;
    color: var(--report-text);
    font-weight: 500;
    text-align: left;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.ranking_meta {
    display: inline-flex;
    align-items: baseline;
    justify-content: flex-end;
    flex-shrink: 0;
    gap: 6px;
    color: var(--report-text-muted);
    font-size: 12px;
    white-space: nowrap;
    text-align: right;
    font-variant-numeric: tabular-nums;

    .ranking_amount {
        color: var(--report-text-muted-strong);
        font-weight: 500;
    }

    .ranking_dot {
        color: var(--report-text-muted-soft);
        font-weight: 700;
    }

    .ranking_share {
        color: var(--report-text-muted);
        font-weight: 500;
    }
}

.empty_state {
    color: var(--report-text-muted);
    font-size: 13px;
    text-align: center;
    padding: 24px 8px;
    margin: auto;
}
</style>
